export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Hydroponics Setup & System Selection',
    subtitle: 'Choose, Build & Start the Right System',
    intro: [
      'Before growing anything, you need a system that actually fits your space, goals and crops.',
      'This volume takes you visually through the major hydroponic setups so you can understand how each one works before investing time and money.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'NFT systems',
      'DWC systems',
      'Kratky systems',
      'Drip hydroponics',
      'Reservoirs',
      'Pumps',
      'Aeration',
      'Air stones',
      'Tubing',
      'Net pots',
      'Growing media',
      'Indoor layouts',
      'Small-space layouts',
      'Grow-light placement',
      'System comparison',
      'Equipment selection',
      'System assembly',
      'Startup checks',
      'Leak and flow checks',
      'Choosing the right system by crop',
    ],
    result:
      'You stop buying equipment blindly and start with a hydroponic setup that actually makes sense for what you want to grow.',
  },
  {
    number: 'Volume 2',
    title: 'Nutrients, pH, EC & Water Management',
    subtitle: 'Control What Your Plants Actually Receive',
    intro: [
      'Plants growing without soil depend entirely on the environment you create for their roots.',
      'That means water quality, nutrients, pH and EC matter.',
      'This volume turns those intimidating numbers into a practical visual system.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Hydroponic nutrients',
      'Macro and micronutrient awareness',
      'Nutrient mixing',
      'Mixing order',
      'Water quality',
      'Reservoir management',
      'pH basics',
      'pH measurement',
      'pH adjustments',
      'EC basics',
      'EC measurement',
      'TDS awareness',
      'Nutrient strength',
      'Plant-stage adjustments',
      'Water level',
      'Reservoir monitoring',
      'Common measurement errors',
      'Visual checking routines',
    ],
    result:
      'Instead of adding nutrients and hoping for the best, you understand what you are measuring and why.',
  },
  {
    number: 'Volume 3',
    title: 'Germination, Roots & Early Plant Growth',
    subtitle: 'Build Strong Plants From the Start',
    intro: [
      'A successful harvest begins long before a plant reaches full size.',
      'Weak seedlings and unhealthy roots can limit the entire grow.',
      'This volume shows you how to take plants through the critical first stages.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Seed selection',
      'Germination',
      'Growing plugs',
      'Seedling development',
      'First roots',
      'Healthy root appearance',
      'Root oxygenation',
      'Seedling lighting',
      'Early nutrient awareness',
      'Transplant timing',
      'Net pot placement',
      'Initial spacing',
      'Plant establishment',
      'Early growth checks',
      'Weak seedlings',
      'Stretching',
      'Poor root development',
      'Common startup mistakes',
    ],
    result:
      'You build stronger plants from the beginning instead of trying to repair weak growth later.',
  },
  {
    number: 'Volume 4',
    title: 'Hydroponic Crop Mastery',
    subtitle: 'Visual Growing Guides for Popular Hydroponic Crops',
    intro: [
      'Not every crop grows the same way.',
      'Lettuce does not behave like tomatoes.',
      'Basil does not require the same space as cucumber.',
      'Strawberries create different challenges than leafy greens.',
      'This volume helps you understand the crop before you grow it.',
    ],
    bulletsIntro: 'Includes visual guides for:',
    bullets: [
      'Lettuce',
      'Basil',
      'Mint',
      'Cilantro',
      'Parsley',
      'Kale',
      'Spinach',
      'Arugula',
      'Tomatoes',
      'Peppers',
      'Cucumbers',
      'Strawberries',
      'Leafy greens',
      'Herbs',
      'Fruiting crops',
    ],
    secondaryBulletsIntro: 'Learn visually:',
    secondaryBullets: [
      'Recommended system types',
      'Plant spacing',
      'Root space',
      'Growth speed',
      'pH working ranges',
      'EC working ranges',
      'Lighting needs',
      'Plant stages',
      'Support requirements',
      'Pruning considerations',
      'Pollination awareness',
      'Harvest readiness',
      'Common crop problems',
    ],
    result:
      'You stop treating every plant the same and start giving each crop the conditions it actually needs.',
  },
  {
    number: 'Volume 5',
    title: 'Lighting, Climate & Yield Optimization',
    subtitle: 'Create Better Conditions for Stronger Growth',
    intro: [
      'Your system can be perfect...',
      'but if your plants have poor lighting, excessive heat, bad airflow or overcrowding, growth can still suffer.',
      'This volume helps you visually understand the environment surrounding the plant.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Grow lights',
      'Light placement',
      'Photoperiod',
      'Light intensity awareness',
      'Plant-to-light distance',
      'Temperature',
      'Humidity',
      'Airflow',
      'Canopy spacing',
      'Plant spacing',
      'Pruning',
      'Plant support',
      'Trellising',
      'Pollination',
      'Growth optimization',
      'Canopy management',
      'Overcrowding',
      'Plant positioning',
      'Environmental checks',
    ],
    result:
      'You create better growing conditions instead of simply waiting and hoping your plants perform well.',
  },
  {
    number: 'Volume 6',
    title: 'Hydroponics Troubleshooting & Recovery',
    subtitle: 'Identify Problems, Check the System & Save the Crop',
    intro: [
      'This is the problem-solving heart of Hydroponics Grower Ready.',
      'Because eventually... something will look wrong.',
      'The difference is whether you panic... or know what to check.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Yellow leaves',
      'Pale growth',
      'Burnt tips',
      'Leaf curling',
      'Leaf spotting',
      'Weak stems',
      'Stretching',
      'Slow growth',
      'Wilting',
      'Root discoloration',
      'Poor root development',
      'Algae',
      'pH instability',
      'EC problems',
      'Nutrient mistakes',
      'Pump problems',
      'Flow problems',
      'Lighting problems',
      'Heat problems',
      'Reservoir issues',
      'System failures',
      'Recovery checks',
      'Troubleshooting trees',
      'Harvest readiness',
    ],
    sequenceIntro: 'Core troubleshooting process:',
    sequence: 'OBSERVE → CHECK → VERIFY → ADJUST → MONITOR',
    resultLead: 'Instead of asking:',
    resultQuote: '“What’s wrong with my plant?”',
    result: 'you have a visual process showing you what to investigate first.',
    popular: true,
    popularLabel: 'PROBLEM SOLVER',
  },
]
